import { Box, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { positiveTrendColorState, negativeTrendColorState } from 'states';
import { formatStrikeIndicatorExpiration } from 'util/optionsCalculator';

const SelectedStrike = React.memo(
  ({
    strike,
    type,
    quantity,
    expiration,
  }: {
    strike: number;
    type: 'call' | 'put';
    quantity: number;
    expiration: number;
  }) => {
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);

    const posServerColor = useRecoilValue(positiveTrendColorState);
    const negServerColor = useRecoilValue(negativeTrendColorState);

    const optionType = type === 'call' ? 'C' : 'P';
    const displayText = `${strike}${optionType}`;
    const isBuy = quantity > 0;
    const placement = type === 'call' ? 'above' : 'below';

    return (
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          zIndex: isHovered ? 5 : 3,
          marginTop: placement === 'above' ? 0 : '4px',
          marginBottom: placement === 'below' ? 0 : '4px',
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            backgroundColor: isBuy
              ? isHovered
                ? posServerColor
                : posServerColor
              : isHovered
                ? negServerColor
                : negServerColor,
            color: isBuy
              ? theme.palette.getContrastText(posServerColor)
              : theme.palette.getContrastText(negServerColor),
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            boxShadow: isHovered ? theme.shadows[3] : theme.shadows[1],
            border: isHovered
              ? `1px solid ${isBuy ? posServerColor : negServerColor}`
              : 'none',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
            '&::after':
              placement === 'above'
                ? {
                    content: '""',
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: `4px solid ${
                      isBuy
                        ? isHovered
                          ? posServerColor
                          : posServerColor
                        : isHovered
                          ? negServerColor
                          : negServerColor
                    }`,
                  }
                : {
                    content: '""',
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderBottom: `4px solid ${
                      isBuy
                        ? isHovered
                          ? posServerColor
                          : posServerColor
                        : isHovered
                          ? negServerColor
                          : negServerColor
                    }`,
                  },
          }}
        >
          {/* Expiration tag */}
          {expiration && (
            <Box
              sx={{
                position: 'absolute',
                ...(isBuy ? { top: -8, right: -6 } : { bottom: -8, right: -6 }),
                backgroundColor: theme.palette.info.main,
                color: theme.palette.info.contrastText,
                fontSize: '0.55rem',
                fontWeight: 'bold',
                padding: '1px 3px',
                borderRadius: '3px',
                lineHeight: 1,
                minWidth: '16px',
                textAlign: 'center',
                boxShadow: theme.shadows[1],
                border: `1px solid ${theme.palette.background.paper}`,
              }}
            >
              {formatStrikeIndicatorExpiration(expiration)}
            </Box>
          )}
          {displayText}
        </Box>
      </Box>
    );
  },
);

export default SelectedStrike;
